<template>
<div class="chapter">
<div class="inner_page">
<div class="section">
<div class="header">
<div class="hgroup">
<h3></h3>
<h1>Chapter 1</h1>
</div>
</div>
<h1>
	1</h1>
<h3>
	Riverside Boardinghouse, Thief River, California, 1891</h3>
<p class="section">
<span class="drop-cap">H</span>old still, dear. I can’t get a straight hem when you wiggle.”</p>
<p>
	Dawn’s movements quieted and she expelled an exasperated sigh. “I’m sorry, Mama. It’s just that I promised Mahalia I’d go into the woods near the river and pick her some berries for dessert tonight.”</p>
<p>
	Libby took the last pin from her mouth and fastened it to the yellow flower-sprigged calico. She caught her daughter’s glance and smiled, admiring how well the bright yellow color suited her dusky complexion.</p>
<p>
	“Walk to the door so I can see if the hem is even.”</p>
<p>
	Dawn pirouetted away, her thick, dark braid swinging.</p>
<p>
	Libby scrutinized her work, satisfied. “It’s nice of you to want to help, but you don’t have to take orders from Mahalia.”</p>
<p>
	Dawn giggled and studied her reflection in the mirror. “Why not, Mama? You do.”</p>
<p>
	Libby caught her daughter’s eye and winked. “I do, don’t I?”</p>
<p>
	“It’s almost like she’s the boss and you’re working for<i> her </i>instead of the other way around.” Laughter lingered in Dawn’s voice as she twirled.</p>
<p>
	“Mahalia does have that effect on people, doesn’t she?” Libby put the pincushion into the sewing basket and set the basket beside the rocking chair. She had mending to do tonight.</p>
<p>
	“I like her, though,” Dawn mused as she swayed to some internal music. “She’ll cook anything I want, and she bakes the most delicious apple pie in the whole world.”</p>
<p>
	“That she does.” Libby’s hands automatically went to her hips, which had rounded slightly more than she would have liked since she’d hired Mahalia as her cook and assistant three years before. She couldn’t call Mahalia a whirlwind, for her larger-than-life appearance and presence likened her more to a tornado.</p>
<p>
	“Have you finished your lessons?”</p>
<p>
	Dawn stopped dancing and wrinkled her nose. “I have sums to do.”</p>
<p>
	Libby swallowed a sigh. Sums were Dawn’s nemesis; this wasn’t the first time she’d finished all of her other lessons, leaving the sums till last, hoping they’d somehow miraculously do themselves.</p>
<p>
	“You can take the dress off, dear<b>.</b>”</p>
<p>
	Dawn stepped out of her new dress, and Libby noticed that the knee of one of her cotton stockings was torn. Dawn attempted to move away, but Libby caught her arm.</p>
<p>
	“What happened to your knee?”</p>
<p>
	“It’s nothing, Mama. Really.”</p>
<p>
	Again she attempted to leave, but Libby drew her close and examined the rip, noting that blood had soaked through the fabric. Gently pushing the cloth aside, she saw the ugly scrape on her daughter’s knee.</p>
<p>
	“How did this happen?”</p>
<p>
	Dawn wouldn’t look at her. “It was nothing, Mama. I … I tripped, that’s all.”</p>
<p>
	An angry ache settled in Libby’s chest. “You were pushed, weren’t you?”</p>
<p>
	Dawn finally pulled away and picked up her school dress. “I told you, I tripped.”</p>
<p>
	Libby clenched her fist and pressed it against her mouth. This was the third time in a week that Dawn had “tripped” on her way home from school, ripping her stockings. But the stockings be damned. Libby refused to believe her daughter was that awkward and clumsy.</p>
<p>
	“It’s those boys, isn’t it? Willie Frost and his bullying friends. They’re teasing you again, aren’t they?”</p>
<p>
	Dawn stood before her in her muslin chemisette. The lace edging the hem of one leg was torn. “It happens all the time, Mama.”</p>
<p>
	How could she be so calm? Libby rose, maternal possessiveness causing her blood to boil in her veins. “I’ll get to the bottom of this if I have to—”</p>
<p>
	“Mama, please,” Dawn pleaded. “If you interfere, you’ll only make things worse.” She hugged Libby’s waist. “It’s not so bad, really. I can usually outrun them. I don’t mind so much. They’ll get tired of picking on me one day and go after someone else.”</p>
<p>
	Libby returned the embrace, pressing her nose against Dawn’s shiny black hair. “But <i>I</i> mind.”</p>
<p>
	Dawn patted her shoulders, as if Libby were the one who needed the encouragement. “I can take care of myself, Mama.”</p>
<p>
	Leaning away from her daughter, Libby swallowed the lump in her throat and gave Dawn’s braid a playful tug. “What will I do when you no longer need me?”</p>
<p>
	“That won’t be for a long, long time,” Dawn assured her.</p>
<p>
	Libby forced a smile. “I guess you need me to tell you to get out of those stockings and have Mahalia bathe your knee.” She lifted an old brown cotton dress off the table. “Put this on before you go cavorting in the woods, please, and hang your school dress in the wardrobe.”</p>
<p>
	Dawn shrugged into the dress while Libby folded the new one and draped it over the sewing chair.</p>
<p>
	“Perhaps you should do your sums before you go off picking berries. I know you. Once you’re in the woods, you’ll have no concept of time, and I’ll have to come looking for you.”</p>
<p>
	“But if I do my sums first, it’ll be dark before I can get to the berry patch.”</p>
<p>
	“Sums come before berry picking, Dawn.”</p>
<p>
	Dawn’s beseeching look was a well-practiced one. Although it confirmed her youth, there were times when Libby swore her daughter was twelve, going on eighteen. The realization filled her with bittersweet emotions.</p>
<p>
	“But, Mama, I—”</p>
<p>
	Laughter erupted on the porch below, and they both turned toward the open window.</p>
<p>
	Dawn skipped across the room, ignoring the cat that slept on the cushioned window seat, and peered outside. “Oh, look! How cute!” She sped past her mother, scrambling to button the last few buttons of her dress before she disappeared out the door. Her footsteps clattered on the stairs.</p>
<p>
	Libby frowned and stepped to the window just as a horse’s rump disappeared beneath the porch roof.</p>
<p>
	Raising her battered head, the cat on the window seat made a raspy sound and glared at Libby with her one good eye.</p>
<p>
	Libby stroked her scarred ears, lingering on her neck. “I’m sorry, Cyclops. I didn’t mean to disturb your nap.”</p>
<p>
	The roof hid the Bellamy brothers from view, but Libby heard them chortling. She wondered how many decades Burl and Bert had been living at the boardinghouse; they’d been here when she arrived twelve years before.</p>
<p>
	Now, as every day, they rocked on the porch, snorting with laughter at something or someone Libby couldn’t see. They passed most days that way, making running commentary on everyone, stranger and acquaintance alike.</p>
<p>
	“What the hell do ya call<i> that?”</i> Burl Bellamy’s cackle turned into a fit of coughing.</p>
<p>
	“It’s a dog.”</p>
<p>
	The voice, rich and deep, tinged with a hint of indignation, reminded Libby of bronze and polished mahogany.</p>
<p>
	“A<i> dog?</i> That ain’t no dog,” Burl argued. “Hell, a real dog’d eat that’n fer lunch and cough up a hair ball bigger’n a stallion’s testicles.”</p>
<p>
	Bert Bellamy howled at his brother’s witticism.</p>
<p>
	Libby’s interest was piqued. She hurried down the stairs and went to the front door, pulling aside the short curtain that covered the window. She peeked outside, squinting at the stranger.</p>
<p>
	“Oh, my.” The words came out on a rush of breath, and she put her hand to her chest, feeling an odd fluttering there.</p>
<p>
	He stood by his mount, big and luxuriantly muscled with a chest as wide as a door and arms as big around as porch pillars. His face was deeply tanned and as leathery as the saddle that was cinched around his horse’s belly. Deep brackets were etched on either side of his mouth, and his jaw was square and hard. Unrelenting, Libby decided.</p>
<p>
	“Oh, what kind is it?” Dawn stood on tiptoe by the stranger’s horse and peered at his saddlebag.</p>
<p>
	Libby’s gaze was riveted on the man’s face, which had softened slightly when he smiled at her daughter. He removed his hat, revealing sun-bleached streaks in hair that was as brown as strong coffee.</p>
<p>
	“It’s called coyote bait, little gal,” Burl suggested, obviously still having a good time at the stranger’s expense.</p>
<p>
	“He’s a Shih Tzu.” The stranger’s smile vanished, and his voice was gruff and defensive.</p>
<p>
	Burl guffawed again. “Hear that, Bert? It’s a shit-soo!”</p>
<p>
	“A shit-Sioux? What’s that?” Bert asked, clearly amused with himself. “Some kinda Injun dog?”</p>
<p>
	“Can’t be, Bert. Ain’t enough of him there to feed a whole tribe.”</p>
<p>
	The brothers chortled again.</p>
<p>
	Dawn glared at the old men. “Shame on both of you. You know how I feel when you make fun of the Indians.”</p>
<p>
	“I’m sorry, little gal,” Burl apologized, still laughing, “but ya gotta admit it ain’t much of a dog<b>.”</b></p>
<p>
	Dawn turned toward the saddlebag again. “I think he’s adorable, especially with that leather thong holding his hair up on the top of his head. Is that to keep it out of his eyes?”</p>
<p>
	The stranger continued to study Dawn, a look on his face that Libby couldn’t identify. She sensed he hadn’t had much experience with girls Dawn’s age. “That’s right, young lady.”</p>
<p>
	Dawn gazed up at him. “Can I hold your dog? Maybe play with him?”</p>
<p>
	He lifted the dog from the saddlebag and handed it to her. “I think Mumser could use some exercise.”</p>
<p>
	“Oooh, Mumser. What a cute name,” Dawn said with a giggle as the pup licked her face. “You’re much more fun than our cranky old cat.”</p>
<p>
	Dawn carried the wiggly pup to the grass, where she ran her fingers over its long, silky coat before it scampered away from her, obviously eager to play.</p>
<p>
	Libby’s gaze lingered on her daughter as the child romped with the pup. She was grateful for Dawn’s resilience. Somehow she had to keep her innocent and sweet, but with the world the way it was, she knew that wasn’t possible. Prejudice against half-bloods was rampant, even in bucolic Thief River, California.</p>
<p>
	Dawn’s laughter tinkled through the air. With such a playmate, her sums certainly would be forgotten, and perhaps even her promise to pick berries.</p>
<p>
	“Is this the boardinghouse?”</p>
<p>
	Again Libby was drawn to the rich timbre of his voice.</p>
<p>
	“Shore is. Riverside. Built in eighteen-seventy on the banks of Thief River by the late Sean O’Malley,” Burl recited. “May God Almighty bless his Irish soul.” He spat a stream of tobacco over the side of the porch, hitting one of Libby’s prize chrysanthemums.</p>
<p>
	With an angry gasp, she flung open the door. “Burl Bellamy! How many times have I told you not to spit your disgusting tobacco onto my flowers?”</p>
<p>
	He turned and grinned, exposing his toothless mouth. “Well, afternoon, Miz Liberty, how long you been standin’ there?”</p>
<p>
	“Long enough to see you do it.” She put her fists on her hips and glared at him. “If you can’t use the spittoon, then quit chawing tobacco.”</p>
<p>
	Lifting her skirt with one hand, she grabbed the sprinkling can she kept on the porch with the other and hurried down the steps to the grass. With her fingertips, she gingerly held the stem of her beautiful pink mum, then doused it with water.</p>
<p>
	“There, there,” she soothed, almost feeling the mum’s anxiety.</p>
<p>
	“Who knows, Miz Liberty? Mebbe tobaccy juice is just what them posies need,” Bert offered.</p>
<p>
	Libby rolled her eyes and swung around. “That stuff is poison. To my flowers<i> and</i> to you.” The last three words lost their punch as she met the stranger’s gaze. She swallowed hard, having momentarily forgotten he was there in the flurry over her mums.</p>
<p>
	His hat was still in his hand. His eyes were such a brilliant blue that they appeared to have been painted.</p>
<p>
	“He’s wantin’ a room, Miz Liberty.”</p>
<p>
	“She don’t rent to folks with dogs,” Burl announced.</p>
<p>
	“Heck, Burl, that ain’t no<i> real</i> dog.”</p>
<p>
	Libby continued to stare at the stranger, her mouth working but nothing coming out. For anyone to render her speechless was quite an accomplishment, she thought, bemused.</p>
<p>
	“Mumser!”</p>
<p>
	Hearing her daughter’s cry of alarm, Libby pulled her gaze to the other side of the path that led to the house, where more of her chrysanthemums grew.</p>
<p>
	“Oh, no!” The damned dog was digging in her precious flower bed!</p>
<p>
	Flinging away the sprinkling can, she flew at the dog, making threatening motions with her hands. “Get away! Shoo! Shoo!”</p>
<p>
	With his rump in the air and his tail wagging, the pup clearly thought Libby wanted to play. She disregarded him and fell to her knees next to the flowers. Ignoring the playful growling and the tugging at her skirt, she replaced the dirt the little beast had dug up around the stems, pressing it over the roots.</p>
<p>
	“I’m sorry, Mama. He just sort of got away from me.” Dawn was contrite as she bent to help her mother put the flowers to rights.</p>
<p>
	“I don’t think he did any real damage.” Libby held a tight rein on her temper, which could be volatile. Although she never displayed anger in front of her daughter, she often felt as if she were going to explode. Like now. It was unreasonable to get emotional over flowers, but she’d worked so hard on them and they were truly the most beautiful mums in northern California. Everyone told her so. Why, perfect strangers would stop and compliment her on their beauty.</p>
<p>
	She took a deep breath and continued to pack the dirt when she heard the keening rip of fabric, followed by Dawn’s gasp and cry.</p>
<p>
	“Cyclops! Mumser! No!”</p>
<p>
	Libby turned in time to see her battered one-eyed cat giving chase to what appeared to be, for all intents and purposes, a small shaggy mop racing over the grass. A length of her own lacy petticoat fluttered along behind the dog.</p>
<p class="section">
	Jackson cringed as he surveyed the chaos and covertly studied the girl. When he’d ridden up, his emotions had been exposed like raw nerves, but he’d quickly shoved them into the corners of his mind, where they belonged. His first glimpse of the girl had nearly done him in.</p>
<p>
	What he’d expected hadn’t been what he’d found. He’d been searching for her for a month, since his return from the Orient. His first discovery was the burned-out village where he’d left her and Grandmother. With mounting fear, he’d tracked her to a ranch, but learned she hadn’t been there for six years. His gut had clenched when he discovered the rancher had been using her as hired help. A mere child, for Christ’s sake! He’d expected to find the same thing here, but found instead a happy, beautiful child, well dressed and cared for by a woman she called Mama. What in the hell was going on?</p>
<p>
	It was hard for him to keep from staring at her. She was a beauty. More than that, she appeared to be sweet-tempered and compassionate. He felt a rush of pride, followed by a surge of guilt that washed every other feeling away.</p>
<p>
	Mumser raced past, breaking into his reverie, the cat not far behind. Mumser was trained to know the “come” and “heel” commands, but at this point, under these circumstances, Jackson wasn’t sure it made any difference. Still, he had to try. He whistled a command, then called his dog. Mumser ignored him, as Jackson knew he would.</p>
<p>
	With his hat in his hand, he crossed to where the woman continued to fuss with the dirt around her posies.</p>
<p>
	When she first stepped onto the porch, he’d noticed her fire. White women were always full of fire. Always had their backs up about one thing or another. They never left a man in peace. But if Jackson thought she showed her temper when the old coot spat tobacco on her flowers, wait until she discovered why he was there.<i> Then</i> he’d see a damned inferno, he had no doubt about that.</p>
<p>
	He hadn’t been drawn to a white woman in over ten years, for all the reasons that had just run through his head. Give him a geisha any day. Or, he thought, remembering painful years passed yet not forgotten, an Indian maiden. There was something soothing about women who knew how to please a man, and to his mind, white women hadn’t quite gotten the hang of it.</p>
<p>
	And he was tired. Damned tired of getting paid to fight someone else’s battles in dirty corners of the world. His years as a globe-trotting freelance mercenary had finally caught up with him. He was ready to retire and settle down. More than ready.</p>
<p>
	“You should really have a fence of some kind around those flowers, ma’am.”</p>
<p>
	The woman stood, her hands on her hips, and gave him an icy stare, although her eyes were dark and hot. “Until today I didn’t have need for one.”</p>
<p>
	He cleared his throat. “The name’s Wolfe, ma’am. Jackson Wolfe.” He bit back another groan as the animals raced past.</p>
<p>
	“Cyclops!” The young girl continued to chase them, her braid, as thick as his fist, swinging from side to side.</p>
<p>
	“After all this,” Jackson began somewhat hesitantly, “I … er … don’t suppose you have a spare room, do you?”</p>
<p>
	Her mouth opened, then snapped shut. “After all this,” she countered, throwing her arms wide with a flourish, “you actually think I’ll rent you … and your<i> dog</i> a room?”</p>
<p>
	“Well, I … er … just came from the jail, and Vern said you might have a room available.”</p>
<p>
	Her gaze was wary. “Vern Roberts?”</p>
<p>
	“Yes, ma’am. I’m acting sheriff until Vern gets back on his feet, and I’ll need a room.” This was where he wanted to stay. No other place would do. He’d camp outside if he had to.</p>
<p>
	She turned away, but not before he saw her jaw clench. “As Burl said, I don’t rent to people with pets.”</p>
<p>
	“I noticed you have a shed out back. That’ll do.”</p>
<p>
	She swung around to face him, her expression incredulous. “You want to sleep in my shed?”</p>
<p>
	Nodding, he added, “I’ll pay you five dollars a week.”</p>
<p>
	Her jaw dropped. “You’ll pay me five dollars a week to sleep in my<i> shed?</i> My regular rooms don’t even cost that much.”</p>
<p>
	The young girl nearly skidded to a stop beside them. “Mama, we have two vacant rooms, and you said we needed—”</p>
<p>
	“Never mind what I said, Dawn. Has Mahalia looked after your skinned knee yet? And what about your sums?” Her voice was stern but not scolding.</p>
<p>
	“But, Mama, you said we needed the money for—”</p>
<p>
	“Ma’am,” Jackson interrupted. “I’ll be gone most days, all day. I’ll take Mumser with me. Why, you won’t even know we’re here.”</p>
<p>
	“Yes, Mama, we won’t even know they’re here.”</p>
<p>
	The girl’s eyes held a familiar twinkle in their depths, and the excitement he saw there weakened him. His gut clenched like a fist.</p>
<p>
	With her arms crossed over her chest, the woman continued to study him. During the fracas, wispy threads of dark hair had come loose from her neat bun and now blew gently over her forehead and cheeks. The rest of her hair shimmered with deep burgundy highlights.</p>
<p>
	Her mouth was rich and full, her skin creamy. There was the suggestion of a cleft in her chin, and a lushness about her that reminded him of sultry Spanish nights. Warm wine. Willing women. His thoughts surprised him, for this one, with her snapping dark eyes, was anything but willing. Though her face was expressionless, those eyes told him she thought that anyone who would spend five dollars a week to sleep in a shed had ulterior motives.</p>
<p>
	If that was what she thought, she’d be right. He’d come for his daughter, Dawn Twilight, and he wouldn’t leave without her.</p>
<p>
</p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chap134659.vue?032c5a02